import React, { useEffect, useMemo, useRef, useState } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, isConfigured } from '../firebase';
import useContent from '../hooks/useContent';
import { saveSection, seedContent, SECTIONS, pendingImports, countPending, importMissing } from '../lib/content';
import { Button, Card, Input, Field, Badge } from './ui';
import MatchesEditor from './MatchesEditor';
import ReportsEditor from './ReportsEditor';
import PlayersEditor from './PlayersEditor';
import GalleryEditor from './GalleryEditor';
import SettingsEditor from './SettingsEditor';
import buildInfo from '../buildInfo.json';
import Diagnostics from './Diagnostics';
import QuickAdd from './QuickAdd';

const TABS = [
  { key: 'karte', label: 'Karte' },
  { key: 'boce', label: 'Boće' },
  { key: 'reports', label: 'Zapisnici' },
  { key: 'players', label: 'Igrači' },
  { key: 'gallery', label: 'Galerija' },
  { key: 'settings', label: 'Tekstovi' },
];

function LoginScreen({ onSubmit, busy, error }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <Card className="w-full max-w-sm p-6">
        <h1 className="text-2xl font-black uppercase tracking-tight text-slate-900">
          Krdo<span className="text-[#009FE3]">•</span>Deva
        </h1>
        <p className="mb-6 text-xs font-bold uppercase tracking-widest text-slate-400">Uredništvo</p>

        <form
          className="space-y-3"
          onSubmit={(e) => { e.preventDefault(); onSubmit(email, password); }}
        >
          <Field label="E-mail">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </Field>
          <Field label="Lozinka">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Field>
          <Button variant="primary" className="w-full" disabled={busy} onClick={() => onSubmit(email, password)}>
            {busy ? 'Prijava…' : 'Prijavi se'}
          </Button>
          <button type="submit" className="hidden" aria-hidden />
          {error && <p className="text-xs font-semibold text-red-600">{error}</p>}
        </form>

        <a href="/" className="mt-6 block text-center text-xs text-slate-400 hover:text-slate-600">← natrag na stranicu</a>
      </Card>
    </div>
  );
}

function NotConfigured() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <Card className="max-w-lg p-6">
        <h1 className="mb-2 text-lg font-bold text-slate-900">Firebase nije konfiguriran</h1>
        <p className="text-sm text-slate-600">
          Napravi datoteku <code className="rounded bg-slate-100 px-1">.env.local</code> u korijenu projekta
          s vrijednostima iz Firebase konzole, pa ponovno pokreni <code className="rounded bg-slate-100 px-1">npm start</code>.
          Detalji su u <code className="rounded bg-slate-100 px-1">SETUP.md</code>.
        </p>
      </Card>
    </div>
  );
}

const SECTION_LABELS = {
  karte: 'mečeva na kartama',
  boce: 'boćanja',
  reports: 'zapisnika',
  players: 'igrača',
  gallery: 'slika',
};

/** First run: fill an empty database from the bundled seed file. */
function SeedPanel({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const seed = seedContent();

  const run = async () => {
    if (!window.confirm('Ovo će upisati sve postojeće rezultate i tekstove u bazu. Nastaviti?')) return;
    setBusy(true);
    setError(null);
    try {
      for (const section of SECTIONS) {
        // eslint-disable-next-line no-await-in-loop
        await saveSection(section, seed[section]);
      }
      onDone();
    } catch (e) {
      setError(e.code === 'permission-denied'
        ? 'Nemaš prava za pisanje — provjeri da si u firestore.rules upisao svoj UID.'
        : e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6 border-amber-300 bg-amber-50 p-4">
      <h2 className="mb-1 text-sm font-bold uppercase tracking-wide text-amber-900">Baza je prazna</h2>
      <p className="mb-3 text-sm text-amber-800">
        Klikni da se {seed.karte.length} mečeva na kartama, {seed.boce.length} boćanja,{' '}
        {seed.reports.length} zapisnika, {seed.players.length} igrača, galerija i svi tekstovi
        prebace u Firestore. Ovo radiš samo jednom.
      </p>
      <Button variant="dark" onClick={run} disabled={busy}>
        {busy ? 'Prebacujem…' : 'Učitaj početne podatke'}
      </Button>
      {error && <p className="mt-2 text-xs font-semibold text-red-700">{error}</p>}
    </Card>
  );
}

/**
 * Shown when the bundled seed file contains records the database doesn't have
 * yet — i.e. a new batch of results was added to the code. Only the missing
 * ids are appended; nothing already in the database is touched.
 */
function ImportPanel({ content, pending, total }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await importMissing(content);
      setDone(true);
    } catch (e) {
      setError(e.code === 'permission-denied'
        ? 'Nemaš prava za pisanje — provjeri da si u firestore.rules upisao svoj UID.'
        : e.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) return null;

  return (
    <Card className="mb-6 border-[#009FE3] bg-[#009FE3]/5 p-4">
      <h2 className="mb-1 text-sm font-bold uppercase tracking-wide text-[#0079ac]">
        Ima {total} novih zapisa za dodati
      </h2>
      <p className="mb-3 text-sm text-slate-700">
        {Object.entries(pending)
          .map(([section, items]) => `${items.length} ${SECTION_LABELS[section] || section}`)
          .join(', ')}
        . Dodaje samo ono čega nema — ništa se ne briše i ne prepisuje.
      </p>
      <Button variant="primary" onClick={run} disabled={busy}>
        {busy ? 'Dodajem…' : `Dodaj ${total} zapisa`}
      </Button>
      {error && <p className="mt-2 text-xs font-semibold text-red-700">{error}</p>}
    </Card>
  );
}

export default function AdminApp() {
  const [user, setUser] = useState(undefined); // undefined = still checking
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [tab, setTab] = useState('karte');
  const [quickAdd, setQuickAdd] = useState(false);

  const { content, loading, source, errors } = useContent();

  useEffect(() => {
    if (!auth) { setUser(null); return undefined; }
    return onAuthStateChanged(auth, (u) => setUser(u));
  }, []);

  const roster = useMemo(
    () => (content.players || []).map((p) => p.statKey).filter(Boolean),
    [content.players]
  );

  const usedStatKeys = useMemo(() => {
    const s = new Set();
    [...(content.karte || []), ...(content.boce || [])].forEach((m) => {
      [...(m.teamA || []), ...(m.teamB || [])].forEach((n) => s.add(n));
    });
    return s;
  }, [content.karte, content.boce]);

  const readFailed = Object.keys(errors || {}).length > 0;

  // An unreadable database is not an empty one — never offer to seed over it.
  const isEmpty = !loading
    && !readFailed
    && (content.karte || []).length === 0
    && (content.boce || []).length === 0
    && (content.players || []).length === 0;

  // Kept fresh every render so two quick saves in a row can't drop the first.
  const contentRef = useRef(content);
  contentRef.current = content;

  const appendMatch = async (sport, match) => {
    const key = sport === 'karte' ? 'karte' : 'boce';
    const list = [...(contentRef.current[key] || []), match];
    contentRef.current = { ...contentRef.current, [key]: list };
    await saveSection(key, list);
  };

  const skipPending = loading || isEmpty || readFailed;
  const pending = useMemo(() => (skipPending ? {} : pendingImports(content)), [content, skipPending]);
  const pendingTotal = useMemo(() => (skipPending ? 0 : countPending(content)), [content, skipPending]);

  if (!isConfigured) return <NotConfigured />;
  if (user === undefined) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Učitavam…</div>;

  if (!user) {
    return (
      <LoginScreen
        busy={busy}
        error={loginError}
        onSubmit={async (email, password) => {
          setBusy(true);
          setLoginError(null);
          try {
            await signInWithEmailAndPassword(auth, email, password);
          } catch (e) {
            setLoginError(
              ['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found'].includes(e.code)
                ? 'Pogrešan e-mail ili lozinka.'
                : e.message
            );
          } finally {
            setBusy(false);
          }
        }}
      />
    );
  }

  if (quickAdd) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-white">
        <QuickAdd
          roster={roster}
          karte={content.karte || []}
          boce={content.boce || []}
          onSave={appendMatch}
          onClose={() => setQuickAdd(false)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <span className="text-lg font-black uppercase tracking-tight text-slate-900">
            Krdo<span className="text-[#009FE3]">•</span>Deva
          </span>
          <Badge tone="blue">Uredništvo</Badge>
          <div className="ml-auto flex items-center gap-2">
            <span
              className="hidden text-[11px] text-slate-400 sm:inline"
              title="Ako se ovdje ne pojavi novo vrijeme nakon deploya, browser ti servira staru verziju — Ctrl+Shift+R."
            >
              build {buildInfo.built}
              {source === 'seed' && ' · podaci iz datoteke, ne iz baze'}
            </span>
            <span className="hidden text-xs text-slate-500 sm:inline">{user.email}</span>
            <a href="/" className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Stranica
            </a>
            <Button onClick={() => signOut(auth)}>Odjava</Button>
          </div>
        </div>

        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4 sm:px-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition ${
                tab === t.key
                  ? 'border-[#009FE3] text-[#009FE3]'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
              {t.key === 'karte' && <span className="ml-1.5 text-xs text-slate-400">{(content.karte || []).length}</span>}
              {t.key === 'boce' && <span className="ml-1.5 text-xs text-slate-400">{(content.boce || []).length}</span>}
              {t.key === 'reports' && <span className="ml-1.5 text-xs text-slate-400">{(content.reports || []).length}</span>}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {loading ? (
          <p className="py-16 text-center text-sm text-slate-400">Učitavam podatke…</p>
        ) : (
          <>
            {!readFailed && !isEmpty && roster.length > 0 && (
              <button
                type="button"
                onClick={() => setQuickAdd(true)}
                className="mb-6 w-full rounded-xl bg-[#009FE3] px-5 py-5 text-left text-white shadow-sm transition active:bg-[#0089c4] sm:flex sm:items-center sm:justify-between"
              >
                <span className="block text-xl font-black">+ Unesi rezultat</span>
                <span className="mt-0.5 block text-sm text-white/80 sm:mt-0">
                  Korak po korak — odabereš tko je dobio, upišeš koliko je imao gubitnik
                </span>
              </button>
            )}

            {readFailed && <Diagnostics uid={user.uid} email={user.email} errors={errors} />}
            {isEmpty && <SeedPanel onDone={() => setTab('karte')} />}
            {!isEmpty && pendingTotal > 0 && (
              <ImportPanel content={content} pending={pending} total={pendingTotal} />
            )}

            {tab === 'karte' && <MatchesEditor key="karte" sport="karte" stored={content.karte} roster={roster} />}
            {tab === 'boce' && <MatchesEditor key="boce" sport="boce" stored={content.boce} roster={roster} />}
            {tab === 'reports' && <ReportsEditor stored={content.reports} karte={content.karte} boce={content.boce} />}
            {tab === 'players' && <PlayersEditor stored={content.players} usedStatKeys={usedStatKeys} />}
            {tab === 'gallery' && <GalleryEditor stored={content.gallery} />}
            {tab === 'settings' && <SettingsEditor stored={content.settings} />}
          </>
        )}
      </main>
    </div>
  );
}
